import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, IconButton, Alert, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Card, CardContent, Stack, Tooltip, Grid,
  Divider, InputAdornment, Tabs, Tab, Autocomplete, Checkbox
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import LockIcon from '@mui/icons-material/Lock';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import FilterListIcon from '@mui/icons-material/FilterList';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  adminGetAllRevenues, adminGetRevenueSummary,
  adminApproveRevenue, adminRejectRevenue,
  adminUpdateRevenue, adminDeleteRevenue, getRevenueAuditLog,
  adminBatchRevenueAction
} from '../../services/revenueService';
import { getAllUsers } from '../../services/authService';
import TelegramIcon from '@mui/icons-material/Telegram';
import SettingsIcon from '@mui/icons-material/Settings';
import SendIcon from '@mui/icons-material/Send';
import axios from 'axios';
import { getUserData } from '../../services/storageService';

const TELEGRAM_API = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/telegram`
  : 'https://backend-c-0mxm.onrender.com/api/telegram';

const telegramApi = axios.create({ baseURL: TELEGRAM_API });
telegramApi.interceptors.request.use((config) => {
  const user = getUserData();
  if (user) config.headers.Authorization = `Bearer ${user.userId || user.token}`;
  return config;
});

const toDisplayableImageUrl = (url) => {
  if (!url) return url;
  if (/\.(heic|heif)$/i.test(url)) {
    return url.replace('/upload/', '/upload/f_jpg/');
  }
  return url;
};

const formatMoney = (amount) => {
  if (!amount && amount !== 0) return '0 ₫';
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 }).format(amount);
};

const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDateTime = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleString('vi-VN', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const statusConfig = {
  draft: { label: 'Nháp', color: 'default', icon: <EditIcon fontSize="small" /> },
  submitted: { label: 'Chờ duyệt', color: 'warning', icon: <HourglassEmptyIcon fontSize="small" /> },
  approved: { label: 'Đã duyệt', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  rejected: { label: 'Từ chối', color: 'error', icon: <CancelIcon fontSize="small" /> },
};

const RevenueManagement = ({ isMobile = false }) => {
  const [revenues, setRevenues] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [userFilter, setUserFilter] = useState(null); // nhân viên đang lọc (object user hoặc null)
  const [usersList, setUsersList] = useState([]);
  const [tabValue, setTabValue] = useState(0);

  const [detailDialog, setDetailDialog] = useState({ open: false, data: null });
  const [rejectDialog, setRejectDialog] = useState({ open: false, id: null, reason: '' });
  const [editDialog, setEditDialog] = useState({ open: false, data: null, totalSales: '', commissionRate: '', reason: '' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, reason: '' });
  const [auditDialog, setAuditDialog] = useState({ open: false, logs: [] });
  const [imagePreview, setImagePreview] = useState({ open: false, url: '' });
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [actionLoading, setActionLoading] = useState(false);

  // Thao tác hàng loạt
  const [selectedIds, setSelectedIds] = useState([]);
  const [processingBatch, setProcessingBatch] = useState(false);
  const [batchRejectDialog, setBatchRejectDialog] = useState({ open: false, reason: '' });

  // Telegram settings
  const [telegramDialog, setTelegramDialog] = useState(false);
  const [telegramConfig, setTelegramConfig] = useState({ botToken: '', chatId: '' });
  const [telegramSaving, setTelegramSaving] = useState(false);
  const [telegramTesting, setTelegramTesting] = useState(false);
  const [telegramEnabled, setTelegramEnabled] = useState(false);

  const showNotification = useCallback((message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  }, []);

  const loadTelegramConfig = async () => {
    try {
      const res = await telegramApi.get('/config');
      if (res.data.success) {
        setTelegramEnabled(res.data.data.enabled);
      }
    } catch (e) {}
  };

  const handleOpenTelegramDialog = async () => {
    setTelegramDialog(true);
    try {
      const res = await telegramApi.get('/config');
      if (res.data.success) {
        setTelegramConfig({
          botToken: res.data.data.botToken || '',
          chatId: res.data.data.chatId || '',
        });
        setTelegramEnabled(res.data.data.enabled);
      }
    } catch (e) {}
  };

  const handleTestTelegram = async () => {
    if (!telegramConfig.botToken || !telegramConfig.chatId) {
      showNotification('Nhập Bot Token và Chat ID trước', 'error');
      return;
    }
    setTelegramTesting(true);
    try {
      const res = await telegramApi.post('/test', telegramConfig);
      showNotification(res.data.message, res.data.success ? 'success' : 'error');
    } catch (e) {
      showNotification(e.response?.data?.message || 'Lỗi kết nối', 'error');
    } finally {
      setTelegramTesting(false);
    }
  };

  const handleSaveTelegram = async () => {
    if (!telegramConfig.botToken || !telegramConfig.chatId) {
      showNotification('Nhập Bot Token và Chat ID', 'error');
      return;
    }
    setTelegramSaving(true);
    try {
      const res = await telegramApi.post('/config', telegramConfig);
      if (res.data.success) {
        showNotification('Đã lưu cấu hình Telegram');
        setTelegramEnabled(true);
        setTelegramDialog(false);
      }
    } catch (e) {
      showNotification(e.response?.data?.message || 'Lỗi', 'error');
    } finally {
      setTelegramSaving(false);
    }
  };

  const fetchRevenues = useCallback(async () => {
    setLoading(true);
    setSelectedIds([]); // bỏ chọn khi đổi trang/filter hoặc tải lại
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (statusFilter) params.status = statusFilter;
      if (userFilter?._id) params.userId = userFilter._id;

      const result = await adminGetAllRevenues(params);
      if (result.success) {
        setRevenues(result.data || []);
        setTotal(result.pagination?.total || 0);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, userFilter]);

  const fetchSummary = useCallback(async () => {
    try {
      const result = await adminGetRevenueSummary();
      if (result.success) setSummary(result.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchRevenues();
  }, [fetchRevenues]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => { loadTelegramConfig(); }, []);

  // Nạp danh sách nhân viên để lọc doanh thu (bỏ admin)
  useEffect(() => {
    (async () => {
      const res = await getAllUsers();
      if (res.success) {
        const list = (res.data || []).filter(u => u.role !== 'admin');
        list.sort((a, b) => (a.fullName || '').localeCompare(b.fullName || '', 'vi'));
        setUsersList(list);
      }
    })();
  }, []);

  const handleApprove = async (id) => {
    if (!window.confirm('Xác nhận duyệt doanh thu này?')) return;
    setActionLoading(true);
    try {
      const result = await adminApproveRevenue(id);
      if (result.success) {
        showNotification('Đã duyệt thành công');
        fetchRevenues();
        fetchSummary();
      } else {
        showNotification(result.error?.message || 'Có lỗi xảy ra', 'error');
      }
    } catch (err) {
      showNotification('Có lỗi xảy ra', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectDialog.reason.trim()) {
      showNotification('Phải nhập lý do từ chối', 'error');
      return;
    }
    setActionLoading(true);
    try {
      const result = await adminRejectRevenue(rejectDialog.id, rejectDialog.reason);
      if (result.success) {
        showNotification('Đã từ chối');
        setRejectDialog({ open: false, id: null, reason: '' });
        fetchRevenues();
        fetchSummary();
      } else {
        showNotification(result.error?.message || 'Có lỗi xảy ra', 'error');
      }
    } catch (err) {
      showNotification('Có lỗi xảy ra', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // ===== Thao tác hàng loạt =====
  const selectableRevenues = revenues.filter((r) => r.status === 'submitted');

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectableRevenues.length > 0 && selectedIds.length === selectableRevenues.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(selectableRevenues.map((r) => r._id));
    }
  };

  const handleBatchApprove = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Xác nhận duyệt ${selectedIds.length} doanh thu đã chọn?`)) return;
    setProcessingBatch(true);
    try {
      const result = await adminBatchRevenueAction(selectedIds, 'approve');
      if (result.success) {
        showNotification(result.data?.message || 'Đã duyệt hàng loạt thành công');
        setSelectedIds([]);
        fetchRevenues();
        fetchSummary();
      } else {
        showNotification(result.error?.message || 'Có lỗi xảy ra', 'error');
      }
    } catch (err) {
      showNotification('Có lỗi xảy ra', 'error');
    } finally {
      setProcessingBatch(false);
    }
  };

  const handleBatchReject = async () => {
    if (!batchRejectDialog.reason.trim()) {
      showNotification('Phải nhập lý do từ chối', 'error');
      return;
    }
    setProcessingBatch(true);
    try {
      const result = await adminBatchRevenueAction(selectedIds, 'reject', batchRejectDialog.reason.trim());
      if (result.success) {
        showNotification(result.data?.message || 'Đã từ chối hàng loạt');
        setBatchRejectDialog({ open: false, reason: '' });
        setSelectedIds([]);
        fetchRevenues();
        fetchSummary();
      } else {
        showNotification(result.error?.message || 'Có lỗi xảy ra', 'error');
      }
    } catch (err) {
      showNotification('Có lỗi xảy ra', 'error');
    } finally {
      setProcessingBatch(false);
    }
  };

  const handleAdminEdit = async () => {
    if (!editDialog.reason.trim()) {
      showNotification('Phải nhập lý do khi sửa', 'error');
      return;
    }
    const parsed = parseFloat(editDialog.totalSales);
    if (isNaN(parsed) || parsed < 0) {
      showNotification('Doanh thu phải là số không âm', 'error');
      return;
    }
    setActionLoading(true);
    try {
      const updateData = {
        totalSales: parsed,
        reason: editDialog.reason,
      };
      // Gửi override commission rate (rỗng = xóa override, dùng lại config mặc định)
      if (editDialog.commissionRate !== '') {
        const rate = parseFloat(editDialog.commissionRate);
        if (isNaN(rate) || rate < 0 || rate > 50) {
          showNotification('% hoa hồng phải từ 0 đến 50', 'error');
          setActionLoading(false);
          return;
        }
        updateData.overrideCommissionRate = rate;
      } else {
        updateData.overrideCommissionRate = null; // Xóa override
      }
      const result = await adminUpdateRevenue(editDialog.data._id, updateData);
      if (result.success) {
        showNotification('Đã cập nhật thành công');
        setEditDialog({ open: false, data: null, totalSales: '', commissionRate: '', reason: '' });
        fetchRevenues();
        fetchSummary();
      } else {
        showNotification(result.error?.message || 'Có lỗi xảy ra', 'error');
      }
    } catch (err) {
      showNotification('Có lỗi xảy ra', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewAudit = async (revenueId) => {
    try {
      const result = await getRevenueAuditLog(revenueId);
      if (result.success) {
        setAuditDialog({ open: true, logs: result.data || [] });
      }
    } catch (err) {
      showNotification('Không thể tải lịch sử', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.reason.trim()) {
      showNotification('Phải nhập lý do xóa', 'error');
      return;
    }
    setActionLoading(true);
    try {
      const result = await adminDeleteRevenue(deleteDialog.id, deleteDialog.reason);
      if (result.success) {
        showNotification('Đã xóa doanh thu thành công');
        setDeleteDialog({ open: false, id: null, reason: '' });
        fetchRevenues();
        fetchSummary();
      } else {
        showNotification(result.error?.message || 'Có lỗi xảy ra', 'error');
      }
    } catch (err) {
      showNotification('Có lỗi xảy ra', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const pendingCount = summary?.pendingCount || 0;

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', border: '1px solid #bfdbfe' }}>
            <CardContent sx={{ py: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <MonetizationOnIcon sx={{ color: '#2563eb' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">Tổng doanh thu (đã duyệt)</Typography>
                  <Typography variant="h6" fontWeight={700} color="#2563eb">
                    {formatMoney(summary?.totals?.totalSales || 0)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', border: '1px solid #bbf7d0' }}>
            <CardContent sx={{ py: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TrendingUpIcon sx={{ color: '#059669' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">Số phiên đã duyệt</Typography>
                  <Typography variant="h6" fontWeight={700} color="#059669">
                    {summary?.totals?.count || 0}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', border: '1px solid #fde68a' }}>
            <CardContent sx={{ py: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <HourglassEmptyIcon sx={{ color: '#d97706' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">Chờ duyệt</Typography>
                  <Typography variant="h6" fontWeight={700} color="#d97706">
                    {pendingCount}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)', border: '1px solid #e9d5ff' }}>
            <CardContent sx={{ py: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <PeopleIcon sx={{ color: '#7c3aed' }} />
                <Box>
                  <Typography variant="caption" color="text.secondary">Số user</Typography>
                  <Typography variant="h6" fontWeight={700} color="#7c3aed">
                    {summary?.byUser?.length || 0}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter and Tab */}
      <Paper sx={{ mb: 2, borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            Quản lý doanh thu livestream
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              icon={<TelegramIcon />}
              label={telegramEnabled ? 'Telegram: Bật' : 'Telegram: Tắt'}
              onClick={handleOpenTelegramDialog}
              sx={{
                cursor: 'pointer', fontWeight: 600,
                bgcolor: telegramEnabled ? '#dcfce7' : '#f1f5f9',
                color: telegramEnabled ? '#16a34a' : '#64748b',
                '& .MuiChip-icon': { color: telegramEnabled ? '#16a34a' : '#64748b' },
              }}
            />
          <Autocomplete
            size="small"
            sx={{ minWidth: 240 }}
            options={usersList}
            value={userFilter}
            onChange={(_, val) => { setUserFilter(val); setPage(0); }}
            getOptionLabel={(o) => o ? `${o.fullName}${o.username ? ` (@${o.username})` : ''}` : ''}
            isOptionEqualToValue={(o, v) => o._id === v._id}
            renderInput={(params) => <TextField {...params} label="Lọc theo nhân viên" placeholder="Tất cả nhân viên" />}
            clearOnEscape
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Trạng thái</InputLabel>
            <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} label="Trạng thái">
              <MenuItem value="">Tất cả</MenuItem>
              <MenuItem value="submitted">Chờ duyệt</MenuItem>
              <MenuItem value="approved">Đã duyệt</MenuItem>
              <MenuItem value="rejected">Từ chối</MenuItem>
            </Select>
          </FormControl>
          </Stack>
        </Box>

        {selectedIds.length > 0 && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
            px: 2, py: 1.5, bgcolor: '#eff6ff', borderTop: '1px solid #dbeafe', borderBottom: '1px solid #dbeafe',
          }}>
            <Typography variant="body2" fontWeight={600} sx={{ mr: 1 }}>
              Đã chọn {selectedIds.length}
            </Typography>
            <Button
              variant="contained" color="success" size="small"
              startIcon={<CheckCircleIcon />} disabled={processingBatch}
              onClick={handleBatchApprove}
            >
              Duyệt hàng loạt
            </Button>
            <Button
              variant="contained" color="error" size="small"
              startIcon={<CancelIcon />} disabled={processingBatch}
              onClick={() => setBatchRejectDialog({ open: true, reason: '' })}
            >
              Từ chối hàng loạt
            </Button>
            <Button
              variant="text" size="small" disabled={processingBatch}
              onClick={() => setSelectedIds([])}
            >
              Bỏ chọn
            </Button>
            {processingBatch && <CircularProgress size={18} sx={{ ml: 1 }} />}
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedIds.length > 0 && selectedIds.length < selectableRevenues.length}
                        checked={selectableRevenues.length > 0 && selectedIds.length === selectableRevenues.length}
                        onChange={handleSelectAll}
                        disabled={selectableRevenues.length === 0}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Ngày</TableCell>
                    {!isMobile && <TableCell sx={{ fontWeight: 700 }}>Giờ live</TableCell>}
                    {!isMobile && <TableCell sx={{ fontWeight: 700 }}>Nền tảng</TableCell>}
                    <TableCell sx={{ fontWeight: 700 }} align="right">Doanh thu</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Bằng chứng</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">Trạng thái</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">Thao tác</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {revenues.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">Không có dữ liệu</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    revenues.map((rev) => {
                      const st = statusConfig[rev.status] || statusConfig.draft;
                      return (
                        <TableRow key={rev._id} hover sx={rev.status === 'submitted' ? { bgcolor: '#fffbeb' } : {}}>
                          <TableCell padding="checkbox">
                            {rev.status === 'submitted' && (
                              <Checkbox
                                checked={selectedIds.includes(rev._id)}
                                onChange={() => handleSelectOne(rev._id)}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {rev.userId?.fullName || 'N/A'}
                            </Typography>
                            {!isMobile && (
                              <Typography variant="caption" color="text.secondary">
                                @{rev.userId?.username}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{formatDate(rev.date)}</TableCell>
                          {!isMobile && <TableCell>{rev.livestreamId?.hours ? `${rev.livestreamId.hours}h` : '-'}</TableCell>}
                          {!isMobile && (
                            <TableCell>
                              <Chip
                                label={rev.platform === 'shopee' ? 'Shopee' : rev.platform === 'tiktok' ? 'TikTok' : 'Khác'}
                                size="small" variant="outlined"
                              />
                            </TableCell>
                          )}
                          <TableCell align="right" sx={{ fontWeight: 600, color: '#059669' }}>
                            {formatMoney(rev.totalSales)}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {(rev.evidence || []).slice(0, 3).map((ev, idx) => (
                                <Box
                                  key={ev._id || idx}
                                  sx={{
                                    width: 36, height: 36, borderRadius: 1, overflow: 'hidden',
                                    border: '1px solid #e2e8f0', cursor: 'pointer',
                                    '&:hover': { opacity: 0.8, transform: 'scale(1.05)' },
                                    transition: 'all 0.2s'
                                  }}
                                  onClick={() => setImagePreview({ open: true, url: toDisplayableImageUrl(ev.url) })}
                                >
                                  <img src={toDisplayableImageUrl(ev.url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </Box>
                              ))}
                              {(rev.evidence || []).length > 3 && (
                                <Chip label={`+${rev.evidence.length - 3}`} size="small" variant="outlined" />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Chip icon={st.icon} label={st.label} color={st.color} size="small" />
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
                              {rev.status === 'submitted' && (
                                <>
                                  <Tooltip title="Duyệt">
                                    <IconButton size="small" color="success" onClick={() => handleApprove(rev._id)} disabled={actionLoading}>
                                      <CheckCircleIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Từ chối">
                                    <IconButton size="small" color="error" onClick={() => setRejectDialog({ open: true, id: rev._id, reason: '' })} disabled={actionLoading}>
                                      <CancelIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                              <Tooltip title="Chi tiết">
                                <IconButton size="small" onClick={() => setDetailDialog({ open: true, data: rev })}>
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Sửa (Admin)">
                                <IconButton
                                  size="small" color="warning"
                                  onClick={() => setEditDialog({
                                    open: true, data: rev,
                                    totalSales: rev.totalSales.toString(),
                                    commissionRate: rev.overrideCommissionRate != null ? rev.overrideCommissionRate.toString() : '',
                                    reason: ''
                                  })}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Xóa">
                                <IconButton
                                  size="small" color="error"
                                  onClick={() => setDeleteDialog({ open: true, id: rev._id, reason: '' })}
                                  disabled={actionLoading}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Lịch sử">
                                <IconButton size="small" onClick={() => handleViewAudit(rev._id)}>
                                  <HistoryIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div" count={total} page={page} rowsPerPage={rowsPerPage}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Số dòng:"
            />
          </>
        )}
      </Paper>

      {/* Revenue by User */}
      {summary?.byUser?.length > 0 && (
        <Paper sx={{ borderRadius: 3, p: 2 }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
            Doanh thu theo User (đã duyệt)
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Tổng doanh thu</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Số phiên</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.byUser.map((u) => (
                  <TableRow key={u.userId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{u.fullName}</Typography>
                      <Typography variant="caption" color="text.secondary">@{u.username}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#059669' }}>
                      {formatMoney(u.totalSales)}
                    </TableCell>
                    <TableCell align="center">{u.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailDialog.open} onClose={() => setDetailDialog({ open: false, data: null })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <MonetizationOnIcon color="primary" /> Chi tiết doanh thu
          {detailDialog.data?.isLocked && <LockIcon color="action" fontSize="small" />}
        </DialogTitle>
        {detailDialog.data && (
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">User</Typography>
                <Typography variant="body1" fontWeight={600}>{detailDialog.data.userId?.fullName}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Ngày</Typography>
                <Typography variant="body1" fontWeight={600}>{formatDate(detailDialog.data.date)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Doanh thu</Typography>
                <Typography variant="h6" fontWeight={700} color="success.main">
                  {formatMoney(detailDialog.data.totalSales)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Nền tảng</Typography>
                <Typography variant="body1">
                  {detailDialog.data.platform === 'shopee' ? 'Shopee' : detailDialog.data.platform === 'tiktok' ? 'TikTok' : 'Khác'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Giờ live</Typography>
                <Typography variant="body1">{detailDialog.data.livestreamId?.hours || '-'}h</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Trạng thái</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    icon={statusConfig[detailDialog.data.status]?.icon}
                    label={statusConfig[detailDialog.data.status]?.label}
                    color={statusConfig[detailDialog.data.status]?.color}
                    size="small"
                  />
                </Box>
              </Grid>
              {detailDialog.data.sessionLink && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Link phiên live</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    <a href={detailDialog.data.sessionLink} target="_blank" rel="noopener noreferrer">
                      {detailDialog.data.sessionLink}
                    </a>
                  </Typography>
                </Grid>
              )}
              {detailDialog.data.sessionCode && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Mã phiên</Typography>
                  <Typography variant="body2">{detailDialog.data.sessionCode}</Typography>
                </Grid>
              )}
              {detailDialog.data.notes && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Ghi chú</Typography>
                  <Typography variant="body2">{detailDialog.data.notes}</Typography>
                </Grid>
              )}
              {detailDialog.data.rejectionReason && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    <strong>Lý do từ chối:</strong> {detailDialog.data.rejectionReason}
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Gửi lúc: {formatDateTime(detailDialog.data.submittedAt)}
                </Typography>
              </Grid>
              {detailDialog.data.approvedBy && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Duyệt bởi: {detailDialog.data.approvedBy?.fullName} - {formatDateTime(detailDialog.data.approvedAt)}
                  </Typography>
                </Grid>
              )}
            </Grid>

            {detailDialog.data.evidence?.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Ảnh bằng chứng ({detailDialog.data.evidence.length})</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {detailDialog.data.evidence.map((ev, idx) => (
                    <Box
                      key={ev._id || idx}
                      sx={{
                        width: 120, height: 120, borderRadius: 1, overflow: 'hidden',
                        border: '1px solid #e2e8f0', cursor: 'pointer',
                        '&:hover': { opacity: 0.8 }, transition: 'all 0.2s'
                      }}
                      onClick={() => setImagePreview({ open: true, url: toDisplayableImageUrl(ev.url) })}
                    >
                      <img src={toDisplayableImageUrl(ev.url)} alt={`evidence-${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </DialogContent>
        )}
        <DialogActions sx={{ p: 2 }}>
          {detailDialog.data?.status === 'submitted' && (
            <>
              <Button
                variant="contained" color="success" startIcon={<CheckCircleIcon />}
                onClick={() => { handleApprove(detailDialog.data._id); setDetailDialog({ open: false, data: null }); }}
                disabled={actionLoading} sx={{ borderRadius: 2 }}
              >
                Duyệt
              </Button>
              <Button
                variant="contained" color="error" startIcon={<CancelIcon />}
                onClick={() => {
                  setRejectDialog({ open: true, id: detailDialog.data._id, reason: '' });
                  setDetailDialog({ open: false, data: null });
                }}
                disabled={actionLoading} sx={{ borderRadius: 2 }}
              >
                Từ chối
              </Button>
            </>
          )}
          <Button onClick={() => setDetailDialog({ open: false, data: null })} variant="outlined" sx={{ borderRadius: 2 }}>
            Đóng
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, id: null, reason: '' })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#dc2626' }}>Từ chối doanh thu</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth multiline rows={3} label="Lý do từ chối *"
            value={rejectDialog.reason}
            onChange={(e) => setRejectDialog({ ...rejectDialog, reason: e.target.value })}
            placeholder="Nhập lý do từ chối để user biết cần sửa gì..."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRejectDialog({ open: false, id: null, reason: '' })} variant="outlined" sx={{ borderRadius: 2 }}>
            Hủy
          </Button>
          <Button
            onClick={handleReject} variant="contained" color="error"
            disabled={actionLoading} sx={{ borderRadius: 2 }}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Xác nhận từ chối'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Reject Dialog */}
      <Dialog open={batchRejectDialog.open} onClose={() => setBatchRejectDialog({ open: false, reason: '' })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#dc2626' }}>Từ chối {selectedIds.length} doanh thu</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth multiline rows={3} label="Lý do từ chối *"
            value={batchRejectDialog.reason}
            onChange={(e) => setBatchRejectDialog({ ...batchRejectDialog, reason: e.target.value })}
            placeholder="Lý do chung sẽ áp dụng cho tất cả doanh thu đã chọn..."
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setBatchRejectDialog({ open: false, reason: '' })} variant="outlined" sx={{ borderRadius: 2 }}>
            Hủy
          </Button>
          <Button
            onClick={handleBatchReject} variant="contained" color="error"
            disabled={processingBatch} sx={{ borderRadius: 2 }}
          >
            {processingBatch ? <CircularProgress size={20} /> : 'Xác nhận từ chối'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Admin Edit Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, data: null, totalSales: '', commissionRate: '', reason: '' })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#d97706' }}>Admin sửa doanh thu</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, mt: 1, borderRadius: 2 }}>
            Thao tác này sẽ được ghi lại trong lịch sử. Bắt buộc nhập lý do.
          </Alert>
          <TextField
            fullWidth type="number" label="Doanh thu (VND)"
            value={editDialog.totalSales}
            onChange={(e) => setEditDialog({ ...editDialog, totalSales: e.target.value })}
            InputProps={{
              startAdornment: <InputAdornment position="start">₫</InputAdornment>,
              inputProps: { min: 0 }
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth type="number" label="% Hoa hồng (để trống = dùng mặc định)"
            value={editDialog.commissionRate}
            onChange={(e) => setEditDialog({ ...editDialog, commissionRate: e.target.value })}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
              inputProps: { min: 0, max: 50, step: 0.1 }
            }}
            placeholder="Ví dụ: 1.5"
            helperText={editDialog.commissionRate !== '' ? `Hoa hồng: ${formatMoney(Math.round(parseFloat(editDialog.totalSales || 0) * parseFloat(editDialog.commissionRate || 0) / 100))}` : 'Để trống sẽ dùng % hoa hồng từ cấu hình mặc định'}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth multiline rows={3} label="Lý do sửa *"
            value={editDialog.reason}
            onChange={(e) => setEditDialog({ ...editDialog, reason: e.target.value })}
            placeholder="Nhập lý do sửa doanh thu..."
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialog({ open: false, data: null, totalSales: '', commissionRate: '', reason: '' })} variant="outlined" sx={{ borderRadius: 2 }}>
            Hủy
          </Button>
          <Button
            onClick={handleAdminEdit} variant="contained" color="warning"
            disabled={actionLoading} sx={{ borderRadius: 2 }}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Lưu thay đổi'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null, reason: '' })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#dc2626' }}>Xóa doanh thu</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2, mt: 1, borderRadius: 2 }}>
            Thao tác này sẽ xóa vĩnh viễn bản ghi doanh thu và ảnh bằng chứng. Bắt buộc nhập lý do.
          </Alert>
          <TextField
            fullWidth multiline rows={3} label="Lý do xóa *"
            value={deleteDialog.reason}
            onChange={(e) => setDeleteDialog({ ...deleteDialog, reason: e.target.value })}
            placeholder="Nhập lý do xóa doanh thu..."
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialog({ open: false, id: null, reason: '' })} variant="outlined" sx={{ borderRadius: 2 }}>
            Hủy
          </Button>
          <Button
            onClick={handleDelete} variant="contained" color="error"
            disabled={actionLoading} sx={{ borderRadius: 2 }}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Xác nhận xóa'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog open={auditDialog.open} onClose={() => setAuditDialog({ open: false, logs: [] })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon color="primary" /> Lịch sử thay đổi
        </DialogTitle>
        <DialogContent>
          {auditDialog.logs.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>Chưa có lịch sử</Typography>
          ) : (
            <Box>
              {auditDialog.logs.map((log, idx) => (
                <Card key={idx} variant="outlined" sx={{ mb: 1, borderRadius: 2 }}>
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Chip
                        label={
                          { created: 'Tạo mới', updated: 'Cập nhật', submitted: 'Gửi duyệt',
                            approved: 'Duyệt', rejected: 'Từ chối', locked: 'Khóa',
                            admin_edited: 'Admin sửa'
                          }[log.action] || log.action
                        }
                        size="small"
                        color={
                          { created: 'info', approved: 'success', rejected: 'error',
                            admin_edited: 'warning'
                          }[log.action] || 'default'
                        }
                      />
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(log.timestamp)}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      Bởi: <strong>{log.performedBy?.fullName || 'N/A'}</strong>
                      {log.performedBy?.role === 'admin' && <Chip label="Admin" size="small" sx={{ ml: 0.5, height: 18 }} />}
                    </Typography>
                    {log.reason && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Lý do: {log.reason}
                      </Typography>
                    )}
                    {log.changes?.length > 0 && (
                      <Box sx={{ mt: 0.5 }}>
                        {log.changes.map((c, ci) => (
                          <Typography key={ci} variant="caption" display="block" color="text.secondary">
                            {c.field}: {c.oldValue != null ? String(c.oldValue) : '(trống)'} → {String(c.newValue)}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAuditDialog({ open: false, logs: [] })} variant="outlined" sx={{ borderRadius: 2 }}>Đóng</Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview */}
      <Dialog open={imagePreview.open} onClose={() => setImagePreview({ open: false, url: '' })} maxWidth="md">
        <DialogContent sx={{ p: 1 }}>
          <img src={imagePreview.url} alt="preview" style={{ maxWidth: '100%', maxHeight: '80vh' }} />
        </DialogContent>
      </Dialog>

      {/* Notification */}
      {/* Telegram Settings Dialog */}
      <Dialog open={telegramDialog} onClose={() => setTelegramDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ background: 'linear-gradient(135deg, #0088cc 0%, #229ED9 100%)', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TelegramIcon sx={{ color: 'white', fontSize: 28 }} />
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>Cấu hình Telegram Bot</Typography>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            Khi user nộp doanh thu, bot sẽ gửi thông báo + ảnh bằng chứng cho bạn. Bạn duyệt/từ chối trực tiếp trên Telegram.
          </Alert>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>Bot Token *</Typography>
          <TextField
            fullWidth size="small" placeholder="123456789:ABCdefGHIjklMNO..."
            value={telegramConfig.botToken}
            onChange={(e) => setTelegramConfig({ ...telegramConfig, botToken: e.target.value })}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, fontFamily: 'monospace', fontSize: '0.85rem' } }}
            helperText="Tạo bot qua @BotFather trên Telegram"
          />
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>Chat ID *</Typography>
          <TextField
            fullWidth size="small" placeholder="123456789"
            value={telegramConfig.chatId}
            onChange={(e) => setTelegramConfig({ ...telegramConfig, chatId: e.target.value })}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, fontFamily: 'monospace' } }}
            helperText="Chat với bot, rồi mở https://api.telegram.org/bot<TOKEN>/getUpdates để lấy chat_id"
          />
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined" onClick={handleTestTelegram} disabled={telegramTesting}
              startIcon={telegramTesting ? <CircularProgress size={16} /> : <SendIcon />}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              Test kết nối
            </Button>
            <Box flex={1} />
            <Button onClick={() => setTelegramDialog(false)} sx={{ borderRadius: 2 }}>Hủy</Button>
            <Button
              variant="contained" onClick={handleSaveTelegram} disabled={telegramSaving}
              startIcon={telegramSaving ? <CircularProgress size={16} /> : <SettingsIcon />}
              sx={{ borderRadius: 2, bgcolor: '#0088cc', textTransform: 'none', fontWeight: 600 }}
            >
              Lưu cấu hình
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={notification.open} autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity} variant="filled" sx={{ borderRadius: 2 }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RevenueManagement;
